import { ClassSchema } from './classes/schemas';
import { createRecordIdCoder, type RecordIdParts } from './common/recordIds';
import { LegacyExamAttemptSchema } from './legacyExamAttempts/schemas';
import { LegacyExamQuestionAttemptSchema } from './legacyExamQuestionAttempts/schemas';
import { LegacyExamSchema } from './legacyExams/schemas';
import { LegacyExamTypeSchema } from './legacyExamTypes/schemas';
import { LegacyTopicGroupSchema } from './legacyTopicGroups/schemas';
import { LegacyTopicSchema } from './legacyTopics/schemas';
import { LessonEnrolmentSchema } from './lessonEnrolments/schemas';
import { LessonSchema } from './lessons/schemas';
import { LessonTemplateSchema } from './lessonTemplates/schemas';
import { NoteSchema } from './notes/schemas';
import { NotesCategorySchema } from './notesCategories/schemas';
import { RoomSchema } from './rooms/schemas';
import { StudentActivityLogSchema } from './studentActivityLog/schemas';
import { StudentCourseStateSchema } from './students/schemas';
import { TutorialSchema } from './tutorials/schemas';
import { UserNoteCreationDatetimeSchema, UserNoteSchema } from './userNotes/schemas';
import { VideoCategorySchema, VideoCategoryTopicSchema } from './videoCategories/schemas';

/**
 * Each kind of record whose id the API encodes from its key, with the fields of its client
 * schema that the key holds, in the order the id holds them. A kind's name and its parts'
 * order never change once shipped, since clients hold its ids.
 */
export const RECORD_ID_PARTS = {
  room: RoomSchema.pick({ location: true, code: true }),
  examType: LegacyExamTypeSchema.pick({ subject: true, type: true }),
  legacyTopic: LegacyTopicSchema.pick({ subject: true, code: true }),
  legacyTopicGroup: LegacyTopicGroupSchema.pick({ subject: true, code: true }),
  videoCategory: VideoCategorySchema.pick({ subject: true, code: true }),
  videoTopic: VideoCategoryTopicSchema.pick({ subject: true, categoryCode: true, code: true }),
  notesCategory: NotesCategorySchema.pick({ subject: true, code: true }),
  note: NoteSchema.pick({ subject: true, categoryCode: true, code: true }),
  // Without the client schema's default, which would build an id from a missing creation time
  userNote: UserNoteSchema.pick({ type: true, ownerId: true }).extend({
    creationDatetime: UserNoteCreationDatetimeSchema,
  }),
  lessonTemplate: LessonTemplateSchema.pick({ courseId: true, code: true }),
  tutorial: TutorialSchema.pick({ courseId: true, startDatetime: true, suffix: true }),
  legacyExam: LegacyExamSchema.pick({ subject: true, code: true }),
  legacyExamAttempt: LegacyExamAttemptSchema.pick({
    studentId: true,
    subject: true,
    examCode: true,
    attemptNumber: true,
  }),
  legacyExamQuestionAttempt: LegacyExamQuestionAttemptSchema.pick({
    studentId: true,
    subject: true,
    examCode: true,
    attemptNumber: true,
    questionId: true,
  }),
  class: ClassSchema.pick({ subject: true, code: true }),
  lesson: LessonSchema.pick({
    subject: true,
    classCode: true,
    courseId: true,
    lessonTemplateCode: true,
    counter: true,
  }),
  lessonEnrolment: LessonEnrolmentSchema.pick({
    studentId: true,
    subject: true,
    classCode: true,
    courseId: true,
    lessonTemplateCode: true,
    counter: true,
  }),
  activityLogEntry: StudentActivityLogSchema.pick({
    studentId: true,
    activityType: true,
    creationDatetime: true,
  }),
  // A shared course state names no stream, which its id holds as null
  courseState: StudentCourseStateSchema.pick({
    studentId: true,
    subject: true,
    courseId: true,
    streamId: true,
  }),
} satisfies Record<string, RecordIdParts>;

export type RecordIdKind = Extract<keyof typeof RECORD_ID_PARTS, string>;

export const { encodeRecordId, decodeRecordId, buildRecordId } =
  createRecordIdCoder(RECORD_ID_PARTS);
